import React from 'react';
import { useQuery } from 'react-apollo';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkButton } from '@atlaskit/button/new';

import { buildCreationLink } from '@confluence/create-blank-fabric-page';
import { ErrorDisplay } from '@confluence/error-boundary';

import { GetPersonalSpaceKeyQuery } from '../GetPersonalSpaceKeyQuery.graphql';
import type { GetPersonalSpaceKeyQuery as GetPersonalSpaceKeyQueryType } from '../__types__/GetPersonalSpaceKeyQuery';

const i18n = defineMessages({
	createPageCTA: {
		id: 'experiment-home-onboarding-takeover-v2.create-page.cta',
		defaultMessage: 'Create page',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will create a page and land them in the editor',
	},
	createWhiteboardCTA: {
		id: 'experiment-home-onboarding-takeover-v2.create-whiteboard.cta',
		defaultMessage: 'Create whiteboard',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will create a whiteboard and land them in the whiteboard editor',
	},
});

type HTV2CreateContentCTA = {
	source: string;
	contentType: 'page' | 'whiteboard';
	onboardingAction: string;
	ctaText: MessageDescriptor;
	appearance: 'primary' | 'default';
};

const HTV2CreateContentCTA = ({
	source,
	contentType,
	onboardingAction,
	ctaText,
	appearance,
}: HTV2CreateContentCTA) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, error } = useQuery<GetPersonalSpaceKeyQueryType>(GetPersonalSpaceKeyQuery);
	const personalSpaceKey =
		(data?.user?.confluence?.hasPersonalSpace && data?.user?.confluence?.space?.key) || '';

	const getButtonHref = (): string => {
		return buildCreationLink({
			contentType,
			source,
			spaceKey: personalSpaceKey,
		});
	};

	const handleClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'homeOnboardingTakeoverV2ActionCta',
				attributes: {
					onboardingAction,
				},
			},
		}).fire();
	};

	return (
		<>
			{error && <ErrorDisplay error={error} />}
			<LinkButton
				onClick={handleClick}
				isDisabled={Boolean(error || !personalSpaceKey)}
				href={getButtonHref()}
				appearance={appearance}
			>
				<FormattedMessage {...ctaText} />
			</LinkButton>
		</>
	);
};

export const HTV2CreatePageCTA = () => {
	return (
		<HTV2CreateContentCTA
			source="homeOnboardingTakeoverV2CreatePage"
			contentType="page"
			onboardingAction="createPage"
			ctaText={i18n.createPageCTA}
			appearance="primary"
		/>
	);
};

export const HTV2CreateWhiteboardCTA = () => {
	return (
		<HTV2CreateContentCTA
			source="homeOnboardingTakeoverV2CreateWhiteboard"
			contentType="whiteboard"
			onboardingAction="createWhiteboard"
			ctaText={i18n.createWhiteboardCTA}
			appearance="default"
		/>
	);
};
